"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Toaster } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const teamContacts = [{
  id: "contact-1",
  name: "Floriana Dos Santos",
  role: "Dirección Académica",
  email: "floriana@ecap.edu.uy",
  phone: "+59896344395",
  initials: "F",
  image: "https://ecap.edu.uy/wp-content/uploads/2026/03/avatar_lucia_rodriguez.webp?v=tto2eomcyql"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage);
    } else if (submitError) {
      toast.error(submitError);
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="98923" className="flex flex-col items-center justify-center space-y-4 rounded-3xl border border-border bg-muted/30 p-8 text-center backdrop-blur-sm">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/20 text-primary">
          <Mail className="size-6" />
        </div>
        <h3 className="text-xl font-bold text-foreground uppercase tracking-tight">¡Solicitud Recibida!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "El nombre completo es requerido",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Nombre y Apellido</FormLabel>
              <FormControl>
                <input type="text" className="flex h-12 w-full rounded-xl border border-input bg-background/50 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" placeholder="Ej: Juan Pérez" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Ingrese su nombre completo para el registro.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es requerido",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Correo Electrónico</FormLabel>
              <FormControl>
                <input type="email" className="flex h-12 w-full rounded-xl border border-input bg-background/50 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" placeholder="tu@email.com" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Le enviaremos la información detallada aquí.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Formato de teléfono inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Celular / WhatsApp</FormLabel>
              <FormControl>
                <input type="tel" className="flex h-12 w-full rounded-xl border border-input bg-background/50 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all" placeholder="099 123 456" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Para coordinar una visita o entrevista.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "El curso de interés es requerido"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Curso de Interés</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12 rounded-xl bg-background/50 border-input">
                    <SelectValue placeholder="Seleccione un área..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Oficios Técnicos">Oficios: Electricidad, Aires, Solar</SelectItem>
                  <SelectItem value="Estética y Belleza">Estética: Barbería, Pestañas</SelectItem>
                  <SelectItem value="Salud y Bienestar">Salud: Masoterapia, Auxiliar</SelectItem>
                  <SelectItem value="Deportes">Deportes: Kinesiología, Entrenamiento</SelectItem>
                  <SelectItem value="Empresariales">Empresariales y Administración</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Su consulta es importante",
      minLength: {
        value: 10,
        message: "Por favor, sea más específico"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Consulta Particular</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-xl border border-input bg-background/50 px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all" placeholder="Cuéntanos tus dudas sobre horarios, pagos o contenidos..." {...field} />
            </FormControl>
            <FormDescription className="text-xs">Indique su ciudad de residencia para derivarlo a la sede más cercana.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <Button type="submit" className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-primary/20">
        Contactanos
      </Button>
    </Form>;
}
export default function EnlaceConComunicaciones() {
  return <section id="conectemos" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-6xl">
        <div className="mb-12 max-w-2xl">
          <h2 className="text-4xl font-extrabold tracking-tighter text-foreground sm:text-5xl mb-4 uppercase">
            Conectemos con <span className="text-secondary">tu Futuro</span>
          </h2>
          <p className="text-lg text-muted-foreground font-medium">
            ECAP está presente en todo el interior de Uruguay. Completa el formulario o contáctanos directamente para iniciar tu camino profesional hoy mismo.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {}
          <div className="lg:col-span-4 space-y-8">
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-foreground uppercase tracking-tight">Atención Directa</h3>
              
              <div className="space-y-4">
                {teamContacts.map((contact, i) => <div key={contact.id} data-index={i} className="flex items-start space-x-4 p-5 rounded-2xl border border-border bg-card/50 backdrop-blur-sm shadow-md transition-all hover:border-primary/50 hover:shadow-lg">
                    <SafeHydrate><Avatar className="size-14 border-2 border-primary/20">
                      <AvatarImage src={contact.image} alt={contact.name} />
                      <AvatarFallback className="bg-primary/20 text-primary font-bold">
                        {contact.initials}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="space-y-1">
                      <h4 className="text-base font-bold text-foreground">{contact.name}</h4>
                      <p className="text-xs font-semibold text-secondary uppercase tracking-wider">{contact.role}</p>
                      <div className="pt-3 space-y-2">
                        <Link to={`mailto:${contact.email}`} className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors font-medium">
                          <Mail className="mr-2 size-4 text-primary" />
                          {contact.email}
                        </Link>
                        <Link to={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors font-medium">
                          <Phone className="mr-2 size-4 text-primary" />
                          {contact.phone}
                        </Link>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="pt-8 border-t border-border space-y-5">
              <h3 className="text-sm font-bold text-foreground uppercase tracking-widest text-secondary">Sede Central & Uruguay</h3>
              <ul className="space-y-4">
                <li className="flex items-start text-sm text-muted-foreground font-medium">
                  <MapPin className="mr-3 size-5 text-primary shrink-0" />
                  <span>Manuel Alvarez 93, Tacuarembó</span>
                </li>
                <li className="flex items-center text-sm text-muted-foreground font-medium">
                  <Mail className="mr-3 size-5 text-primary shrink-0" />
                  <span>info@ecap.edu.uy</span>
                </li>
                <li className="flex items-center text-sm text-muted-foreground font-medium">
                  <Phone className="mr-3 size-5 text-primary shrink-0" />
                  <span>+59896344395</span>
                </li>
              </ul>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-10 shadow-2xl shadow-black/10 backdrop-blur-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl" />
              <div className="mb-8 relative">
                <h3 className="text-2xl font-bold text-foreground uppercase tracking-tight">Formulario de Inscripción</h3>
                <p className="text-sm text-muted-foreground mt-2 font-medium">
                  Déjanos tus datos y un asesor académico te contactará en las próximas 24 horas hábiles.
                </p>
              </div>

              <FormProvider formId="ecap_contacto_general" sectionName="Contacto ECAP Academy" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} submitText="Solicitar Información" successMessage="Tu consulta ha sido enviada. Pronto nos comunicaremos contigo para brindarte todos los detalles del curso." errorMessage="Hubo un inconveniente al procesar tu solicitud. Por favor, intenta nuevamente o llámanos.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" richColors />
    </section>;
}